import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, X, ChevronDown, RotateCcw } from 'lucide-react';
import { useOnboarding } from '../../hooks/useOnboarding';
import { OnboardingChecklist } from './OnboardingChecklist';
import { OnboardingSpotlight } from './OnboardingSpotlight';
import { OnboardingTooltip } from './OnboardingTooltip';

interface OnboardingWidgetProps {
  className?: string;
}

export function OnboardingWidget({ className = '' }: OnboardingWidgetProps) {
  const navigate = useNavigate();
  const {
    progress,
    loading,
    shouldShowWidget,
    highlightedElement,
    highlightMessage,
    showChecklist,
    dismiss,
    reset,
    clearHighlight,
    setShowChecklist,
  } = useOnboarding();

  const [isMinimized, setIsMinimized] = useState(false);
  const [isOpen, setIsOpen] = useState(true);
  // Track if widget was force-opened via menu (overrides shouldShowWidget)
  const [forceOpen, setForceOpen] = useState(false);
  // Track if a modal is open (hide tooltip while modal is visible)
  const [modalOpen, setModalOpen] = useState(false);
  // Track if a guide is actively showing tooltips (hide widget tooltip)
  const [guideActive, setGuideActive] = useState(false);

  // Auto-show checklist when progress updates
  useEffect(() => {
    if (showChecklist) {
      setIsMinimized(false);
      setIsOpen(true);
    }
  }, [showChecklist]);

  // Listen for event to open widget fully (from ProfileDropdown "Guide de démarrage")
  // This hook MUST be before any early returns to always be registered
  useEffect(() => {
    const handleOpenWidget = () => {
      localStorage.setItem('onboarding:guide-active', 'true');
      window.dispatchEvent(new CustomEvent('onboarding:guide-activated'));
      setForceOpen(true);
      setIsOpen(true);
      setIsMinimized(false);
    };
    window.addEventListener('onboarding:open-widget', handleOpenWidget);
    return () => {
      window.removeEventListener('onboarding:open-widget', handleOpenWidget);
    };
  }, []);

  // Reset forceOpen when shouldShowWidget becomes true again
  useEffect(() => {
    if (shouldShowWidget) {
      setForceOpen(false);
    }
  }, [shouldShowWidget]);

  // Listen for auto-minimize/expand events
  // Minimize when starting steps, expand when completing a theme
  useEffect(() => {
    const handleMinimize = () => {
      setIsMinimized(true);
      setModalOpen(false);
    };
    const handleExpand = () => {
      setIsMinimized(false);
      setIsOpen(true);
      setModalOpen(false);
    };
    const handleModalOpen = () => setModalOpen(true);
    const handleGuideActive = () => setGuideActive(true);
    const handleGuideInactive = () => setGuideActive(false);
    
    window.addEventListener('onboarding:widget-minimize', handleMinimize);
    window.addEventListener('onboarding:widget-expand', handleExpand);
    window.addEventListener('onboarding:modal-open', handleModalOpen);
    window.addEventListener('onboarding:guide-active', handleGuideActive);
    window.addEventListener('onboarding:guide-inactive', handleGuideInactive);
    return () => {
      window.removeEventListener('onboarding:widget-minimize', handleMinimize);
      window.removeEventListener('onboarding:widget-expand', handleExpand);
      window.removeEventListener('onboarding:modal-open', handleModalOpen);
      window.removeEventListener('onboarding:guide-active', handleGuideActive);
      window.removeEventListener('onboarding:guide-inactive', handleGuideInactive);
    };
  }, []);

  // Don't render if loading
  if (loading) {
    return null;
  }

  // Don't render if shouldn't show AND not force-opened
  if (!shouldShowWidget && !forceOpen) {
    return null;
  }

  // For 'not_started' status, only show widget if force-opened via menu
  // (Let DiscoveryTooltip guide user to click "Guide de démarrage" first)
  if (progress?.status === 'not_started' && !forceOpen) {
    return null;
  }

  // Don't render if no progress data
  if (!progress) {
    return null;
  }

  const handleDismiss = async () => {
    try {
      await dismiss();
      setIsOpen(false);
      setForceOpen(false); // Reset force-open state on dismiss
      localStorage.setItem('onboarding:guide-active', 'false');
      window.dispatchEvent(new CustomEvent('onboarding:guide-deactivated'));
    } catch (error) {
      console.error('Failed to dismiss onboarding:', error);
    }
  };

  // Minimized state - show bubble with remaining steps
  if (isMinimized) {
    const remainingSteps = progress.steps.length - progress.steps_completed.length;
    return (
      <>
        <OnboardingSpotlight
          elementId={highlightedElement}
          message={highlightMessage}
          onClose={clearHighlight}
        />
        <button
          onClick={() => {
            localStorage.setItem('onboarding:guide-active', 'true');
            window.dispatchEvent(new CustomEvent('onboarding:guide-activated'));
            setIsMinimized(false);
          }}
          className={`fixed bottom-6 right-6 z-50 flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all ${className}`}
          aria-label="Ouvrir le guide de démarrage"
        >
          <MessageCircle className="w-6 h-6" />
          {remainingSteps > 0 && (
            <span className="absolute -top-1 -right-1 flex items-center justify-center w-5 h-5 text-xs font-bold rounded-full bg-amber-400 text-amber-900">
              {remainingSteps}
            </span>
          )}
        </button>
      </>
    );
  }

  // Widget closed completely
  if (!isOpen) {
    return null;
  }

  // Determine which step needs a tooltip "Continuer ici" or "Cliquez ici pour commencer"
  const getNextStepTooltip = () => {
    if (!progress) return null;
    
    const completedSet = new Set(progress.steps_completed);
    const orderedSteps = ['welcome', 'profile', 'first_agent', 'first_knowledge', 'first_test'];
    
    for (const stepId of orderedSteps) {
      if (!completedSet.has(stepId)) {
        return {
          stepId,
          message: stepId === 'welcome' ? 'Cliquez ici pour commencer' : 'Continuer ici',
        };
      }
    }
    return null;
  };

  const nextStepTooltip = getNextStepTooltip();

  return (
    <>
      {/* Spotlight overlay */}
      <OnboardingSpotlight
        elementId={highlightedElement}
        message={highlightMessage}
        onClose={clearHighlight}
      />

      {/* Step guidance tooltip - hide when modal is open or guide is active */}
      {nextStepTooltip && !modalOpen && !guideActive && (
        <OnboardingTooltip
          targetSelector={`[data-onboarding="step-${nextStepTooltip.stepId}"]`}
          title={nextStepTooltip.message}
          description=""
          step={1}
          totalSteps={1}
          position="left"
          isVisible={true}
          showNextButton={false}
        />
      )}

      {/* Widget container */}
      <div 
        className={`fixed bottom-6 right-6 z-50 w-80 transition-all duration-300 ${isMinimized ? 'h-auto' : ''} ${className}`}
      >
        {/* Widget card */}
        <div className="relative">
          {/* Action buttons */}
          <div className="absolute -top-2 -right-2 flex gap-1 z-10">
            {/* Minimize button */}
            <button
              onClick={() => setIsMinimized(true)}
              className="flex items-center justify-center w-7 h-7 rounded-full bg-white dark:bg-zinc-700 text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 shadow-md hover:shadow-lg transition-all"
              aria-label="Réduire"
              title="Réduire"
            >
              <ChevronDown className="w-3.5 h-3.5" />
            </button>
            {/* Reset button */}
            <button
              onClick={async () => {
                try {
                  await reset();
                  setIsOpen(false); // Collapse widget after reset
                  localStorage.setItem('onboarding:guide-active', 'false');
                  window.dispatchEvent(new CustomEvent('onboarding:guide-deactivated'));
                  navigate('/');
                } catch (error) {
                  console.error('Failed to reset onboarding:', error);
                }
              }}
              className="flex items-center justify-center w-7 h-7 rounded-full bg-white dark:bg-zinc-700 text-zinc-500 hover:text-emerald-600 dark:hover:text-emerald-400 shadow-md hover:shadow-lg transition-all"
              aria-label="Recommencer le guide"
              title="Recommencer le guide"
            >
              <RotateCcw className="w-3.5 h-3.5" />
            </button>
            {/* Close button */}
            <button
              onClick={handleDismiss}
              className="flex items-center justify-center w-7 h-7 rounded-full bg-white dark:bg-zinc-700 text-zinc-500 hover:text-red-500 dark:hover:text-red-400 shadow-md hover:shadow-lg transition-all"
              aria-label="Fermer le guide"
              title="Fermer le guide"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Checklist */}
          <OnboardingChecklist
            progress={progress}
          />
        </div>
      </div>
    </>
  );
}

export default OnboardingWidget;
